import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame } from "lucide-react";

// Sprint 8.A.2.5 — Flexpfad-Serie im Cockpit-Header.
// Tägliche Pull-Mechanik: animierter Counter 0→N beim Mount, pulsierende Flamme
// wenn heute schon aktiv, kurzer Roter-Blink bei broken=true.
// Pro: volle Coach-Voice; Free: nur Nummer + Click → Preview-Modal.

function useAnimatedNumber(target, duration = 900) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const goal = Number(target) || 0;
    if (goal <= 0) {
      setN(0);
      return undefined;
    }
    const steps = Math.min(goal, 40);
    const stepDelay = duration / steps;
    let i = 0;
    const timer = setInterval(() => {
      i += 1;
      setN(Math.round(goal * (i / steps)));
      if (i >= steps) clearInterval(timer);
    }, stepDelay);
    return () => clearInterval(timer);
  }, [target, duration]);
  return n;
}

export default function StreakCounter({ streak, isPro, onUpgradeClick }) {
  const current = streak?.current_streak ?? 0;
  const broken = !!streak?.broken;
  const activeToday = !!streak?.active_today;
  const voice = (streak?.voice || "").trim();

  const display = useAnimatedNumber(current);
  const [blink, setBlink] = useState(false);

  // Roter Blink wenn Flexpfad-Serie gerade gebrochen
  useEffect(() => {
    if (broken && current === 0) {
      setBlink(true);
      const t = setTimeout(() => setBlink(false), 700);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [broken, current]);

  const isInactive = current === 0;
  const flameClass = activeToday && !isInactive
    ? "text-orange-500"
    : isInactive
    ? "text-slate-300"
    : "text-amber-500";

  const bg = blink
    ? "bg-red-50 border-red-200"
    : activeToday
    ? "bg-gradient-to-r from-amber-50 to-orange-100 border-amber-200"
    : isInactive
    ? "bg-slate-50 border-slate-200"
    : "bg-amber-50 border-amber-100";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      data-testid="streak-counter"
      className={`mt-3 inline-flex items-center gap-2.5 rounded-xl border px-3.5 py-2 ${bg}`}
      onClick={!isPro && onUpgradeClick ? onUpgradeClick : undefined}
      style={!isPro && onUpgradeClick ? { cursor: "pointer" } : undefined}
      role={!isPro && onUpgradeClick ? "button" : undefined}
      title={!isPro ? "Mit Pro Solo: Detail-Coach-Voice + Wochen-Report" : undefined}
    >
      <motion.span
        animate={activeToday && !isInactive ? { scale: [1, 1.18, 1] } : { scale: 1 }}
        transition={
          activeToday && !isInactive
            ? { duration: 0.85, repeat: Infinity, ease: "easeInOut" }
            : {}
        }
        className={`inline-flex ${flameClass}`}
        aria-hidden="true"
      >
        <Flame className="w-5 h-5" fill="currentColor" fillOpacity={isInactive ? 0 : 0.25} />
      </motion.span>

      <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 leading-tight">
        <span
          data-testid="streak-counter-value"
          className={`text-sm font-extrabold ${isInactive ? "text-slate-500" : "text-amber-900"}`}
        >
          {display} Tage Flexpfad-Serie
          {activeToday && !isInactive ? " ✓" : ""}
        </span>
        {isPro && voice && (
          <span
            data-testid="streak-counter-voice"
            className="text-xs text-slate-600"
          >
            {voice}
          </span>
        )}
        {!isPro && (
          <span
            data-testid="streak-counter-locked-hint"
            className="text-xs text-slate-500 italic"
          >
            🔒 Detail-Analyse mit Pro Solo
          </span>
        )}
      </div>
    </motion.div>
  );
}
